import type { ListBuilderColumn } from '../../types';
import { STOCK_LEVEL_VALUE_MAPPING } from './shared';

/**
 * Creates the location code column with description
 */
export const createLocationCodeColumn = (): ListBuilderColumn => ({
	id: 'location-code',
	fields: [
		{
			id: 'code-field',
			link: {
				type: 'object',
				enabled: true,
				objectTable: 'location',
				idField: 'id',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'code',
					objectTable: 'location',
				},
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'description-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'description',
					objectTable: 'location',
				},
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h1', text: 'Code' }],
	minWidth: 150,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'code',
			objectTable: 'location',
		},
	],
	widthConfig: {
		mode: 'flexible',
		preset: 'large',
	},
	verticalAlignment: 'top',
});

/**
 * Creates the location type column (used in default-locations-all)
 */
export const createLocationTypeColumn = (): ListBuilderColumn => ({
	id: 'location-type',
	fields: [
		{
			id: 'type-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'typeFormatted',
					objectTable: 'location',
				},
				defaultValue: 'Unknown',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h2', text: 'Type' }],
	minWidth: 120,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'type',
			objectTable: 'location',
		},
	],
	widthConfig: {
		mode: 'fixed',
		width: 120,
	},
	verticalAlignment: 'top',
});

/**
 * Creates the location info column (used in default-locations-all)
 */
export const createBasicLocationInfoColumn = (): ListBuilderColumn => ({
	id: 'location-info',
	fields: [
		{
			id: 'locationInfo-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'locationInfo',
					objectTable: 'location',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h3', text: 'Location Info' }],
	minWidth: 200,
	sortable: false,
	alignment: 'left',
	widthConfig: {
		mode: 'flexible',
		preset: 'large',
	},
	verticalAlignment: 'top',
});

/**
 * Creates the capacity column.
 *
 * Reads `location.totalCapacity` — the backend enrichment in
 * `locations.service.ts` that aggregates `SUM(location_item.capacity)`
 * across the items at the location. The legacy template wrote
 * `{location.capacity} {location.units}` which read the raw column on
 * the Location entity itself — that column is NULL on the motor_city_ice
 * tenant (and likely others), so the Capacity column rendered "-" even
 * when the merchandiser had real capacity-bearing items configured.
 * Defect #12 in docs/claude/_archive/motor-city-defects/register.md.
 *
 * `units` on the entity is a single value, but the aggregate spans
 * multiple items that may carry different units — so we render a bare
 * number here. Per-item unit context is shown when the user drills into
 * the location's items list.
 */
export const createCapacityColumn = (
	headerText = 'Capacity',
	headerId = 'h4',
): ListBuilderColumn => ({
	id: 'capacity',
	fields: [
		{
			id: 'capacity-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'totalCapacity',
					objectTable: 'location',
				},
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: headerId, text: headerText }],
	minWidth: 100,
	// `totalCapacity` is a backend enrichment, not a DB column, so the
	// list-builder sort path can't reach it. Mirrors how the totalQuantity
	// column is treated below.
	sortable: false,
	alignment: 'left',
	widthConfig: {
		mode: 'fixed',
		width: 100,
	},
	verticalAlignment: 'top',
});

/**
 * Creates the status column
 */
export const createStatusColumn = (headerId = 'h5'): ListBuilderColumn => ({
	id: 'status',
	fields: [
		{
			id: 'status-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'statusFormatted',
					objectTable: 'location',
				},
				defaultValue: 'Unknown',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: headerId, text: 'Status' }],
	minWidth: 100,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'status',
			objectTable: 'location',
		},
	],
	widthConfig: {
		mode: 'fixed',
		width: 100,
	},
	verticalAlignment: 'top',
});

/**
 * Creates the actions column with actions dropdown
 */
export const createActionsColumn = (): ListBuilderColumn => ({
	id: 'actions',
	fields: [
		{
			id: 'actions-dropdown',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'edit-action',
					label: 'Edit',
					iconName: 'edit',
					callbackName: 'edit',
					variant: 'primary',
					visibility: 'visible',
				},
				{
					id: 'delete-action',
					label: 'Delete',
					iconName: 'trash',
					callbackName: 'delete',
					variant: 'danger',
					visibility: 'visible',
				},
			],
		},
	],
	headers: [{ id: 'actions-header', text: '' }],
	width: 80,
	align: 'center',
	sortable: false,
});

/**
 * Creates the client info column with address (used in merchandiser lists)
 */
export const createMerchandiserClientInfoColumn = (): ListBuilderColumn => ({
	id: 'location-info',
	fields: [
		{
			id: 'clientName-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'clientName',
					objectTable: 'location',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'addressLine1-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressLine1',
					objectTable: 'location',
				},
				defaultValue: '',
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-2',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'addressLine2-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressLine2',
					objectTable: 'location',
				},
				defaultValue: '',
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h2', text: 'Client' }],
	minWidth: 200,
	sortable: false,
	alignment: 'left',
	widthConfig: {
		mode: 'flexible',
		preset: 'large',
	},
	verticalAlignment: 'top',
});

/**
 * Creates the items count column
 */
export const createItemsCountColumn = (): ListBuilderColumn => ({
	id: 'items-count',
	fields: [
		{
			id: 'itemsCount-field',
			link: {
				type: 'callback',
				enabled: true,
				callbackName: 'showItems',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'itemsCount',
					objectTable: 'location',
				},
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'statusInfo',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h3', text: 'Items' }],
	minWidth: 80,
	sortable: false,
	alignment: 'center',
	widthConfig: {
		mode: 'fixed',
		width: 80,
	},
	verticalAlignment: 'top',
});

/**
 * Creates the total quantity column
 */
export const createTotalQuantityColumn = (): ListBuilderColumn => ({
	id: 'total-quantity',
	fields: [
		{
			id: 'totalQuantity-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'totalQuantity',
					objectTable: 'location',
				},
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h4', text: 'Qty' }],
	minWidth: 80,
	sortable: false,
	alignment: 'center',
	widthConfig: {
		mode: 'fixed',
		width: 80,
	},
	verticalAlignment: 'top',
});

/**
 * Creates the fill percentage column
 */
export const createFillPercentageColumn = (): ListBuilderColumn => ({
	id: 'fill-percentage',
	fields: [
		{
			id: 'fillPercentage-field',
			dataConfig: {
				type: 'template',
				template: '{location.fillPercentage}%',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-fill', text: 'Fill %' }],
	minWidth: 80,
	sortable: false,
	alignment: 'center',
	widthConfig: {
		mode: 'fixed',
		width: 80,
	},
	verticalAlignment: 'top',
});

/**
 * Creates the stock level badge column
 */
export const createStockLevelColumn = (): ListBuilderColumn => ({
	id: 'stock-level',
	fields: [
		{
			id: 'stockLevel-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'stockLevel',
					objectTable: 'location',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				valueMapping: [...STOCK_LEVEL_VALUE_MAPPING],
			},
			displayStyle: 'badge',
		},
	],
	headers: [{ id: 'h-level', text: 'Level' }],
	minWidth: 90,
	sortable: false,
	alignment: 'center',
	widthConfig: {
		mode: 'fixed',
		width: 90,
	},
	verticalAlignment: 'top',
});

/**
 * Creates the temperature column (for high-temp-merchandisers)
 */
export const createTemperatureColumn = (): ListBuilderColumn => ({
	id: 'temperature',
	fields: [
		{
			id: 'temperature-field',
			dataConfig: {
				type: 'template',
				template: '{location.currentTemperature}°F',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-temp', text: 'Temp' }],
	minWidth: 80,
	sortable: false,
	alignment: 'center',
	widthConfig: {
		mode: 'fixed',
		width: 80,
	},
	verticalAlignment: 'top',
});

/**
 * Creates columns for the default-locations-all list
 */
export const createDefaultLocationColumns = (): Array<ListBuilderColumn> => [
	createLocationCodeColumn(),
	createLocationTypeColumn(),
	createBasicLocationInfoColumn(),
	createCapacityColumn(),
	createStatusColumn(),
	createActionsColumn(),
];

/**
 * Creates columns for merchandiser lists (standard)
 */
export const createMerchandiserColumns = (): Array<ListBuilderColumn> => [
	createLocationCodeColumn(),
	createMerchandiserClientInfoColumn(),
	createItemsCountColumn(),
	createTotalQuantityColumn(),
	createFillPercentageColumn(),
	createStockLevelColumn(),
	createStatusColumn(),
	createActionsColumn(),
];

/**
 * Creates columns for low-stock-merchandisers (includes capacity and adjusted header)
 */
export const createLowStockMerchandiserColumns =
	(): Array<ListBuilderColumn> => [
		createLocationCodeColumn(),
		createMerchandiserClientInfoColumn(),
		createItemsCountColumn(),
		createTotalQuantityColumn(),
		createFillPercentageColumn(),
		createStockLevelColumn(),
		createCapacityColumn('Capacity', 'h5'),
		createStatusColumn('h6'),
		createActionsColumn(),
	];

/**
 * Creates columns for high-temp-merchandisers (includes temperature)
 */
export const createHighTempMerchandiserColumns =
	(): Array<ListBuilderColumn> => [
		createLocationCodeColumn(),
		createMerchandiserClientInfoColumn(),
		createTemperatureColumn(),
		createItemsCountColumn(),
		createFillPercentageColumn(),
		createStockLevelColumn(),
		createStatusColumn(),
		createActionsColumn(),
	];
